import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { IntPolluxResult } from '../../../models/generic-pollux-result';
import { DataContract, DataContractListEntry } from '../models/data-contract';
import { SupportedFieldDropdown } from '../../app-settings/models/supported-field-dropdown';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';

/**
 * Dataverse service
 */
@Injectable({
  providedIn: 'root'
})
export class DataverseService {
  /**
   * Data librabry state
   */
  public SHOW_DATA_LIBRARY = 'DATA_LIBRARY';

  /**
   * Data contract state
   */
  public SHOW_DATA_CONTRACTS = 'DATA_CONTRACTS';

  /**
   * Widget designer state
   */
  public SHOW_WIDGET_DESIGNER = 'WIDGET_DESIGNER';

  /**
   * Field editor state
   */
  public SHOW_FIELD_EDITOR = 'FIELD_EDITOR';

  /**
   * Edit field propertoes state
   */
  public SHOW_EDIT_FIELD_PROPERTIES = 'SHOW_EDIT_FIELD_PROPERTIES';

  /**
   * Search results "virtual" BO name
   */
  public SEARCH_RESULTS_BO_NAME = 'SearchResults';

  /**
   * Edited field subject
   */
  private editedFieldSubject = new BehaviorSubject<any>(null);

  /**
   * Edited field observable
   */
  public editedField = this.editedFieldSubject.asObservable();

  /**
   * Display state subject
   */
  private displayStateSubject = new BehaviorSubject<string>(this.SHOW_DATA_LIBRARY);

  /**
   * Display state observable
   */
  public displayState$ = this.displayStateSubject.asObservable();

  /**
   * Selected fields
   */
  private selectedFields: any[] = [];

  /**
   * Selected fields list subject
   */
  private selectedFieldsListSubject = new BehaviorSubject<any[]>([]);

  /**
   * Selected fields list observable
   */
  public selectedFieldsList = this.selectedFieldsListSubject.asObservable();

  /**
   * Fields list subject
   */
  private fieldsListSubject = new BehaviorSubject<any>(null);

  /**
   * Fields list observable
   */
  public fieldsList = this.fieldsListSubject.asObservable();

  /**
   * Business objects subject
   */
  private businessObjectsSubject = new BehaviorSubject<any>(null);

  /**
   * Business objects observable
   */
  public businessObjects$ = this.businessObjectsSubject.asObservable();

  /**
   * Fields subject
   */
  private fieldsSubject = new BehaviorSubject<any>(null);

  /**
   * Fields observable
   */
  public fields$ = this.fieldsSubject.asObservable();

  /**
   * Validation results subject
   */
  private validationResultsSubject = new BehaviorSubject<any[]>([]);

  /**
   * Validation results observable
   */
  public validationResults = this.validationResultsSubject.asObservable();

  /**
   * Where is it used fields list subject
   */
  private whereIsItUsedFieldsListSubject = new BehaviorSubject<any[]>([]);

  /**
   * Where is it used fields list observable
   */
  public whereIsItUsedFieldsList = this.whereIsItUsedFieldsListSubject.asObservable();

  /**
   * Business objects
   */
  public businessObjects: any[] = [];

  /**
  * Unique names subject
  */
  private uniqueNamesSubject = new BehaviorSubject<string[]>([]);

  /**
   * Unique names observable
   */
  public uniqueNames$ = this.uniqueNamesSubject.asObservable();

  /**
   * Data loaded subject
   */
  public dataLoaded = new BehaviorSubject<boolean>(false);

  /**
   * Data loaded observable
   */
  public dataLoaded$ = this.dataLoaded.asObservable();

  /**
   * Search field opened subject
   */
  public searchFieldOpened = new BehaviorSubject<boolean>(false);

  /**
   * Data loaded observable
   */
  public searchFieldOpened$ = this.searchFieldOpened.asObservable();

  /**
   * Field types
   */
  public fieldTypes: any[] = [
    'Undocumented',
    'Text',
    'Numeric',
    'Date',
    'Bool',
    'SingleSelect',
    'MultiSelect',
    'Retriever',
    'System'
  ];

  /**
   * Versions
   */
  public versions: any[] = [
    'Dev',
    'Release'
  ];

  /**
   * Loaded fields
   */
  public loadedFields: any = [];

  /**
   * JSON for creating a new field
   */
  public newFieldJson: any = {
    "oid": -1,
    "documentation": "",
    "dto_name": "",
    "dtoNameJson": "",
    "fieldName": "",
    "group": null,
    "isArea": false,
    "isCalculated": false,
    "isCurrency": false,
    "isLength": false,
    "isPercentage": false,
    "uniqueName": "",
    "mockupValue": "",
    "type": "Text",
    "version": "Dev",
    "dataType": "Field",
    "Category1": "",
    "Category2": "",
    "obsolete": false,
    "precision": null,
    "referenceTableEntries": null,
    "scale": null,
    "sqlDescriptionField": null,
    "sqlFieldName": "",
    "sqlFieldType": "Text",
    "sqlIdField": null,
    "sqlReferenceTable": null,
    "sqlTableName": "",
    "systemField": false,
    "calculation": null,
    "createdOrModifiedManually": true,
    "IsDataStore": true,
    "BoName": "",
    "MappingComment1": "",
    "MappingComment2": "",
    "DefinitionEn": "",
    "DefinitionDe": "",
    "hasWarning": false,
    "selected": true
  };

  /**
   * Center pane scroll  Y
   */
  public centerPaneScrollY = 0;

  /**
   * Constructor
   * @param httpClient
   * @param appServiceHelper
   */
  constructor(private httpClient: HttpClient, private appServiceHelper: AppServiceHelperService) { }

  /**
  * Inserts supported dropdowns fields for field
  * @param fields Supported fields
  */
  upsertSupportedFieldDropdown(fields: SupportedFieldDropdown[]): Observable<any> {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', 'dataverse/v2/data/field/supportedFieldsDropdown'), fields);
  }

  /**
* Gets supported dropdowns fields for field and reflist
* @param fieldId fieldid
* @param refList refList
*/
  getSupportedFieldDropdown(fieldId: number, refList: string): Observable<SupportedFieldDropdown[]> {
    return this.httpClient.get<SupportedFieldDropdown[]>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/field/supportedFieldsDropdown?fieldId=${fieldId}&refList=${refList}`));
  }


  /**
   * Initiate a download
   * @param blob BLOB item
   * @param alternateFilename File name
   */
  initiateDownload(blob: any, alternateFilename: string) {
    const element = document.createElement('a');
    element.setAttribute('href', window.URL.createObjectURL(blob));
    element.setAttribute('download', alternateFilename);
    element.style.display = 'none';
    document.body.appendChild(element);
    setTimeout(()=> {
      element.click();
      setTimeout(()=> {
        document.body.removeChild(element);
      }, 100);
    }, 150);
  }

  /**
   * Filters the fields
   * @param config Filter config
   */
  public filter(config: any) {
    const result = {};

    if (!config) {
      this.fieldsListSubject.next(result);
      return result;
    }

    if (config.businessObjects?.length) {
      this.handleFieldsForBo(config, result);
    }

    if (!config.selectDataSeries) {
      for (const [key, _value] of Object.entries(result)) {
        result[key] = result[key].filter((en) => {
          return en.dataType !== 'DataSeries';
        });
      }
    }

    if (!config.selectFields) {
      for (const [key, _value] of Object.entries(result)) {
        result[key] = result[key].filter((en) => {
          return en.dataType !== 'Field';
        });
      }
    }

    if (config.resetSearch && this.businessObjects.filter((bo) => { return bo.name === this.SEARCH_RESULTS_BO_NAME; }, this).length) {
      this.businessObjects = this.businessObjects.filter((bo) => { return bo.name !== this.SEARCH_RESULTS_BO_NAME; }, this);
      this.businessObjectsSubject.next(this.businessObjects);
    }

    this.fieldsListSubject.next(result);
    return result;
  }
  /**
   * Handle fields for this BO
   * @param config
   * @param result
   */

  private handleFieldsForBo(config: any, result: any): void {
    for (const bo of config.businessObjects.filter((bo) => { return bo.selected; })) {
      let fieldsForBo;
      if (config.searchTerm) {
        fieldsForBo = this.loadedFields.filter((field) => {
          return field.uniqueName.toLowerCase().indexOf(config.searchTerm.toLowerCase()) >= 0;
        });
      }
      else {
        fieldsForBo = this.loadedFields.filter((field) => {
          return field.BoName === bo.name;
        });
      }
      result[bo.name] = fieldsForBo.filter((field) => { return !field.systemField; });
    }
  }

  /**
   * Toggle a field selection
   * @param field The field
   */
  public toggleFieldSelected(field: any) {
    field.selected = !field.selected;
    if (this.selectedFields.indexOf(field) >= 0) {
      this.selectedFields = this.selectedFields.filter((en) => { return en !== field; });
      this.setEditedField(null);
      this.setValidationResults([]);
    }
    else {
      this.selectedFields.push(field);
    }

    this.selectedFieldsListSubject.next(this.selectedFields);
  }

  /**
  * Toggle fields selection for specified bo
  * @param fields Bo fields
  * @param toggleState State to toggle to (true = selected)
  */
  public toggleBoSelectedFields(fields: Array<any>, toggleState: boolean) {
    for (const field of fields) {
      field.selected = toggleState;
      const selectedField = this.selectedFields.find(f => f.oid === field.oid);
      if (toggleState) {
        if (!selectedField) {
          // if not there => add
          this.selectedFields.push(field);
        }
      } else if (selectedField) {
        // if there => remove
        const idx = this.selectedFields.indexOf(selectedField);
        this.selectedFields.splice(idx, 1);
      }
    }

    this.setEditedField(null);
    this.setValidationResults([]);
    this.selectedFieldsListSubject.next(this.selectedFields);
  }


  /**
   * Sets the new display state
   * @param state The new display state
   */
  public setDisplayState(state: string) {
    this.displayStateSubject.next(state);
  }

  /**
   * Sets the edited field
   * @param field Field to be edited
   */
  public setEditedField(field: any) {
    this.editedFieldSubject.next(field);
  }

  /**
   * Sets the validation results
   * @param results The validation results
   */
  public setValidationResults(results: Array<any>) {
    this.validationResultsSubject.next(results);
  }

  //#region Data contracts

  /**
   * Data contracts list subject
   */
  private dataContractsListSubject = new BehaviorSubject<DataContractListEntry[]>([]);

  /**
   * Data contracts list observable
   */
  public dataContractsList$ = this.dataContractsListSubject.asObservable();

  /**
  * Load Data contracts
  */
  public loadDataContracts(): void {
    this.httpClient.get<DataContractListEntry[]>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', 'dataverse/datacontract/list'))
      .pipe(take(1)).subscribe((result) => {
        this.dataContractsListSubject.next(result);
      });
  }

  /**
  * Load Data contract
  */
  public loadDataContract(oid: number): Promise<DataContract> {
    return new Promise<DataContract>((resolve) => {
      this.httpClient.get<DataContract>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/datacontract/${oid}`))
        .pipe(take(1)).subscribe((result) => {
          resolve(result);
        });
    });
  }

  /**
  * Save Data contract
  */
  public saveDataContract(dataContract: DataContract): Promise<IntPolluxResult> {
    return new Promise<IntPolluxResult>((resolve) => {
      this.httpClient.post<IntPolluxResult>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/datacontract`), dataContract)
        .pipe(take(1)).subscribe((result) => {
          resolve(result);
        });
    });
  }

  /**
  * Load unique names
  */
  public loadUniqueNames(): Promise<string[]> {
    return new Promise<string[]>((resolve) => {
      this.httpClient.get<string[]>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/datacontract/uniqenames/all`))
        .pipe(take(1)).subscribe((result) => {
          this.uniqueNamesSubject.next(result);
        });
    });
  }

  /**
  * Currently selected Data contract
  */
  private selectedDataContractSubject = new BehaviorSubject<DataContractListEntry>(null);

  /**
   * Currently selected Data contract observable
   */
  public selectedDataContract$ = this.selectedDataContractSubject.asObservable();

  /**
   * Set selected Data contract
   */
  public setSelectedDataContract(dataContract: DataContractListEntry) {
    this.selectedDataContractSubject.next(dataContract);
  }

  /**
   * Search
   * @param term
   */
  public search(term: string) {
    if (term) {
      // start search only if search term is >= 3 characters
      if (term.length >= 3) {
        let searchResultBo;
        const bosMatching = this.businessObjects.filter((bo) => { return bo.name === this.SEARCH_RESULTS_BO_NAME; }, this);
        if (bosMatching?.length) {
          searchResultBo = bosMatching[0];
          searchResultBo.selected = true;
        }
        else {
          searchResultBo = { name: this.SEARCH_RESULTS_BO_NAME, fieldCount: 0, selected: true, hidden: true };
          this.businessObjects.splice(0, 0, searchResultBo);
        }

        const searchResults = this.filter({
          businessObjects: [searchResultBo],
          selectFields: true,
          selectDataSeries: true,
          searchTerm: term
        });

        if (searchResults[this.SEARCH_RESULTS_BO_NAME]) {
          searchResultBo.fieldCount = searchResults[this.SEARCH_RESULTS_BO_NAME].length;
        }
        else {
          searchResultBo.fieldCount = 0;
        }

        // if search happened, make all BOs in left pane unselected
        for (const bo of this.businessObjects) {
          bo.selected = false;
        }

        this.setDisplayState(this.SHOW_DATA_LIBRARY);
        this.setEditedField(null); //close edit form if opened
        this.businessObjectsSubject.next(this.businessObjects);
      }
    }
    else {
      this.businessObjects = this.businessObjects.filter((bo) => { return bo.name !== this.SEARCH_RESULTS_BO_NAME; }, this);
      this.businessObjectsSubject.next(this.businessObjects);

      this.filter(null);
    }
  }

  //#endregion
}
